import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, EmptyState, StatCard } from "@/components/ui-bits";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Eye, Search, Wallet, Boxes, AlertTriangle, Wrench, CheckCircle2, BarChart3, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { AssetImageUploader, ImageChip } from "@/components/AssetImageUploader";

const CATEGORIES = ["Laptop","TV","Tablet","Chair","Desk","Printer","Projector","Electronics","Classroom Equipment","Office Equipment","Sports","Books","Other"];
const CONDITIONS = ["new","excellent","good","fair","poor"];
const STATUSES = ["serviceable","under_maintenance","unserviceable","disposed","lost"];

const NGN = (v) => "₦" + (Number(v) || 0).toLocaleString();

const STATUS_STYLE = {
  serviceable: "bg-emerald-50 text-emerald-700 border-emerald-200",
  under_maintenance: "bg-amber-50 text-amber-700 border-amber-200",
  unserviceable: "bg-red-50 text-red-700 border-red-200",
  disposed: "bg-slate-100 text-slate-600 border-slate-200",
  lost: "bg-red-100 text-red-800 border-red-300",
};

const CONDITION_STYLE = {
  new: "bg-emerald-50 text-emerald-700",
  excellent: "bg-emerald-50 text-emerald-700",
  good: "bg-blue-50 text-blue-700",
  fair: "bg-amber-50 text-amber-700",
  poor: "bg-red-50 text-red-700",
};

const emptyForm = {
  name: "", asset_tag: "", category: "Laptop", quantity: 1, location: "",
  assigned_to_staff_name: "", purchase_date: "", purchase_cost: "",
  supplier: "", serial_number: "", model_number: "", warranty_until: "",
  condition: "good", status: "serviceable", notes: "",
  photos: [], // staged client-side until after create
};

const Assets = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({ q: "", category: "all", status: "all", condition: "all" });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const canManage = ["super_admin", "director", "inventory_officer"].includes(user?.role) || (user?.roles || []).some(r => ["super_admin","director","inventory_officer"].includes(r));

  const load = async () => {
    try {
      const params = {};
      if (filters.q) params.q = filters.q;
      if (filters.category !== "all") params.category = filters.category;
      if (filters.status !== "all") params.status = filters.status;
      if (filters.condition !== "all") params.condition = filters.condition;
      const [a, s] = await Promise.all([api.get("/assets", { params }), api.get("/assets/stats")]);
      setList(a.data); setStats(s.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const onSearch = () => load();

  const submit = async () => {
    try {
      const { photos, ...rest } = form;
      const payload = {
        ...rest,
        quantity: Number(form.quantity) || 1,
        purchase_cost: form.purchase_cost ? Number(form.purchase_cost) : undefined,
      };
      const { data } = await api.post("/assets", payload);
      // Upload any staged photos immediately after creation
      if (photos && photos.length > 0) {
        for (let i = 0; i < photos.length; i++) {
          const p = photos[i];
          await api.post(`/assets/${data.id}/images`, {
            kind: i === 0 ? "main" : "gallery",
            name: p.name, type: p.type, data: p.data,
          });
        }
      }
      toast.success(`Created ${data.asset_tag}${photos.length ? ` · ${photos.length} photo${photos.length > 1 ? "s" : ""}` : ""}`);
      setOpen(false); setForm(emptyForm); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="assets-page">
      <PageHeader
        overline="Asset Register"
        title="Fixed Assets & School Properties"
        description="Track laptops, TVs, projectors, classroom equipment and every other fixed asset across the academy — with full lifecycle history."
        actions={canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#1B2C5A] hover:bg-[#14224A] text-white" data-testid="new-asset-btn"><Plus className="w-4 h-4 mr-1.5" /> New Asset</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display text-[#1B2C5A]">Register Asset</DialogTitle></DialogHeader>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                <Fld label="Asset Name" full><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="asset-name" /></Fld>
                <Fld label="Asset Tag"><Input value={form.asset_tag} onChange={(e) => setForm({ ...form, asset_tag: e.target.value })} placeholder="Auto-generated" /></Fld>
                <Fld label="Category">
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="asset-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </Fld>
                <Fld label="Quantity"><Input type="number" min="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} /></Fld>
                <Fld label="Location"><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Primary 5 Classroom A" /></Fld>
                <Fld label="Assigned Staff"><Input value={form.assigned_to_staff_name} onChange={(e) => setForm({ ...form, assigned_to_staff_name: e.target.value })} /></Fld>
                <Fld label="Purchase Date"><Input type="date" value={form.purchase_date} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} /></Fld>
                <Fld label="Purchase Cost (₦)"><Input type="number" value={form.purchase_cost} onChange={(e) => setForm({ ...form, purchase_cost: e.target.value })} /></Fld>
                <Fld label="Supplier"><Input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} /></Fld>
                <Fld label="Model Number"><Input value={form.model_number} onChange={(e) => setForm({ ...form, model_number: e.target.value })} /></Fld>
                <Fld label="Serial Number"><Input value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} /></Fld>
                <Fld label="Warranty Until"><Input type="date" value={form.warranty_until} onChange={(e) => setForm({ ...form, warranty_until: e.target.value })} /></Fld>
                <Fld label="Condition">
                  <Select value={form.condition} onValueChange={(v) => setForm({ ...form, condition: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{CONDITIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </Fld>
                <Fld label="Status">
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                  </Select>
                </Fld>
                <Fld label="Notes" full><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Fld>
                <div className="md:col-span-2 space-y-1.5">
                  <Label className="text-[#1B2C5A] text-sm flex items-center gap-1.5"><ImagePlus className="w-4 h-4 text-[#9DCD3F]" /> Asset Photos (Optional)</Label>
                  <AssetImageUploader
                    multiple
                    label="Drop photos here or click to upload"
                    onPick={(files) => setForm((f) => ({ ...f, photos: [...(f.photos || []), ...files].slice(0, 6) }))}
                  />
                  {form.photos && form.photos.length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-2" data-testid="new-asset-photos-preview">
                      {form.photos.map((p, i) => (
                        <ImageChip key={i} src={p.data} name={p.name} onRemove={() => setForm((f) => ({ ...f, photos: f.photos.filter((_, j) => j !== i) }))} />
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-[#475569]">The first photo becomes the main display image. You can manage more later from the asset profile.</p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={submit} className="bg-[#1B2C5A] hover:bg-[#14224A] text-white" data-testid="asset-submit">Register Asset</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      />

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <StatCard label="Total Assets" value={stats.total_assets} icon={Boxes} testId="stat-total" />
          <StatCard label="Total Value" value={NGN(stats.total_value)} icon={Wallet} accent testId="stat-value" />
          <StatCard label="Serviceable" value={(stats.by_status?.serviceable?.count) || 0} icon={CheckCircle2} testId="stat-serviceable" />
          <StatCard label="Under Maintenance" value={(stats.by_status?.under_maintenance?.count) || 0} icon={Wrench} testId="stat-maintenance" />
          <StatCard label="Open Faults" value={stats.open_faults || 0} icon={AlertTriangle} testId="stat-faults" />
        </div>
      )}

      {/* Filters */}
      <Card className="p-4 border-[#E2E8F0] mb-4">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="relative lg:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#475569]" />
            <Input
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && onSearch()}
              placeholder="Search name, tag or serial..."
              className="pl-9 h-10 bg-white"
              data-testid="filter-q"
            />
          </div>
          <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
            <SelectTrigger data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={onSearch} className="bg-[#1B2C5A] hover:bg-[#14224A] text-white" data-testid="apply-filters">Apply Filters</Button>
        </div>
      </Card>

      {list.length === 0 ? <EmptyState message="No assets matching your filters." /> : (
        <Card className="border-[#E2E8F0] overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#FDFBF7]">
                <TableHead className="w-16">Photo</TableHead>
                <TableHead>Asset Tag</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Qty</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Condition</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map((a) => (
                <TableRow key={a.id} data-testid={`asset-row-${a.id}`}>
                  <TableCell>
                    {a.photo ? (
                      <img src={a.photo} alt={a.name} className="w-12 h-12 rounded-md object-cover border border-[#E2E8F0] cursor-pointer hover:ring-2 hover:ring-[#9DCD3F]" loading="lazy" onClick={() => navigate(`/app/assets/${a.id}`)} />
                    ) : (
                      <button
                        onClick={() => navigate(`/app/assets/${a.id}`)}
                        className="w-12 h-12 rounded-md bg-[#1B2C5A]/5 border border-dashed border-[#1B2C5A]/30 hover:border-[#9DCD3F] hover:bg-[#9DCD3F]/10 grid place-items-center text-[#1B2C5A]/60 transition"
                        title="Add a photo"
                        data-testid={`asset-add-photo-${a.id}`}
                      >
                        <ImagePlus className="w-4 h-4" />
                      </button>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{a.asset_tag}</TableCell>
                  <TableCell className="font-medium text-[#1B2C5A]">{a.name}</TableCell>
                  <TableCell>{a.category}</TableCell>
                  <TableCell>{a.quantity}</TableCell>
                  <TableCell className="text-sm">{a.location || "—"}</TableCell>
                  <TableCell><span className={"text-xs px-2 py-0.5 rounded-full " + (CONDITION_STYLE[a.condition] || "")}>{a.condition}</span></TableCell>
                  <TableCell><span className={"text-xs px-2 py-0.5 rounded-full border " + (STATUS_STYLE[a.status] || "")}>{(a.status || "").replace(/_/g, " ")}</span></TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" onClick={() => navigate(`/app/assets/${a.id}`)} className="border-[#E2E8F0]" data-testid={`asset-view-${a.id}`}>
                      <Eye className="w-3.5 h-3.5 mr-1" /> View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
};

const Fld = ({ label, children, full }) => (
  <div className={"space-y-1.5 " + (full ? "md:col-span-2" : "")}><Label className="text-[#1B2C5A] text-sm">{label}</Label>{children}</div>
);

export default Assets;
